import * as THREE from 'three';

export class CoinPiles {
    private container: HTMLElement;
    private scene: THREE.Scene;
    private camera: THREE.PerspectiveCamera;
    private renderer: THREE.WebGLRenderer;
    private coins: CoinObject[] = [];
    private animationId: number | null = null;
    private disposables: (THREE.Material | THREE.Geometry | THREE.BufferGeometry | THREE.Texture)[] = [];

    constructor(container: HTMLElement) {
        this.container = container;
        this.init();
    }

    private init() {
        // Scene setup
        this.scene = new THREE.Scene();
        // Transparent background to blend with app
        this.scene.background = null;

        // Camera setup
        // Camera setup
        const width = this.container.clientWidth || 300; // Fallback
        const height = this.container.clientHeight || 500; // Fallback
        const aspect = width / height;
        this.camera = new THREE.PerspectiveCamera(45, aspect, 0.1, 100);
        this.camera.position.set(0, 4, 8);
        this.camera.lookAt(0, 1.5, 0);

        // Renderer setup
        this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        this.renderer.setSize(width, height);
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.container.appendChild(this.renderer.domElement);

        // Lighting
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.8); // Increased from 0.6
        this.scene.add(ambientLight);

        const dirLight = new THREE.DirectionalLight(0xffffff, 1.8); // Increased from 1.2
        dirLight.position.set(5, 10, 5);
        dirLight.castShadow = true;
        dirLight.shadow.mapSize.width = 1024;
        dirLight.shadow.mapSize.height = 1024;
        this.scene.add(dirLight);

        const fillLight = new THREE.DirectionalLight(0xffd700, 0.8); // Increased from 0.5
        fillLight.position.set(-5, 5, 5);
        this.scene.add(fillLight);

        // Point light for specular highlights
        const pointLight = new THREE.PointLight(0xffffff, 50, 100); // Increased from 1
        pointLight.position.set(0, 5, 2);
        this.scene.add(pointLight);

        // Ground plane (shadow catcher)
        const planeGeometry = new THREE.PlaneGeometry(20, 20);
        const planeMaterial = new THREE.ShadowMaterial({ opacity: 0.15 });
        const plane = new THREE.Mesh(planeGeometry, planeMaterial);
        plane.rotation.x = -Math.PI / 2;
        plane.receiveShadow = true;
        this.scene.add(plane);
        this.disposables.push(planeGeometry, planeMaterial);

        // Create Coins
        this.createCoins();

        // Start Animation
        this.animate();

        // Handle Resize
        window.addEventListener('resize', this.handleResize);
    }

    private createRibbedTexture(): THREE.CanvasTexture {
        const canvas = document.createElement('canvas');
        canvas.width = 512;
        canvas.height = 64;
        const context = canvas.getContext('2d');
        if (context) {
            context.fillStyle = '#ffd700';
            context.fillRect(0, 0, 512, 64);

            // Create ribs (vertical stripes)
            const numRibs = 120;
            const ribWidth = 512 / numRibs;

            for (let i = 0; i < numRibs; i++) {
                const x = i * ribWidth;
                // Darker stripe for the groove
                context.fillStyle = i % 2 === 0 ? '#b8860b' : '#ffd700';
                context.fillRect(x, 0, ribWidth, 64);
            }
        }
        const texture = new THREE.CanvasTexture(canvas);
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;
        this.disposables.push(texture);
        return texture;
    }

    private createDollarTexture(): THREE.CanvasTexture {
        const canvas = document.createElement('canvas');
        canvas.width = 256;
        canvas.height = 256;
        const context = canvas.getContext('2d');
        if (context) {
            // Gold background
            context.fillStyle = '#ffd700';
            context.fillRect(0, 0, 256, 256);

            // Inner ring
            context.beginPath();
            context.arc(128, 128, 110, 0, Math.PI * 2);
            context.lineWidth = 8;
            context.strokeStyle = '#b8860b'; // Dark golden rod
            context.stroke();

            // Dollar Sign
            context.font = 'bold 160px sans-serif';
            context.textAlign = 'center';
            context.textBaseline = 'middle';
            context.fillStyle = '#b8860b';
            context.fillText('$', 128, 138);
        }
        const texture = new THREE.CanvasTexture(canvas);
        this.disposables.push(texture);
        return texture;
    }

    private createCoins() {
        const scaleSize = 1.5;
        const coinRadius = 0.6 * scaleSize;
        const coinHeight = 0.15 * scaleSize;
        const coinSegments = 64;

        const coinGeometry = new THREE.CylinderGeometry(coinRadius, coinRadius, coinHeight, coinSegments);
        this.disposables.push(coinGeometry);

        // 1. Plain Material (for bottom coin)
        const plainMaterial = new THREE.MeshPhongMaterial({
            color: 0xffd700,
            specular: 0xffffff,
            shininess: 100,
            emissive: 0x443300,
            emissiveIntensity: 0.2
        });
        this.disposables.push(plainMaterial);

        // 2. Textured Materials
        const ribbedTexture = this.createRibbedTexture();
        const dollarTexture = this.createDollarTexture();

        // Side Material (Ribs)
        // Side Material (Ribs)
        const sideMaterial = new THREE.MeshPhongMaterial({
            color: 0xffd700,
            map: ribbedTexture,
            bumpMap: ribbedTexture,
            bumpScale: 0.05,
            specular: 0xffffff,
            shininess: 150, // Increased from 80
            emissive: 0x443300,
            emissiveIntensity: 0.4 // Increased from 0.2
        });
        this.disposables.push(sideMaterial);

        // Face Material (Dollar)
        // Face Material (Dollar)
        const faceMaterial = new THREE.MeshPhongMaterial({
            color: 0xffd700,
            map: dollarTexture,
            bumpMap: dollarTexture,
            bumpScale: 0.05,
            specular: 0xffffff,
            shininess: 150, // Increased from 80
            emissive: 0x443300,
            emissiveIntensity: 0.4 // Increased from 0.2
        });
        this.disposables.push(faceMaterial);

        // Array of materials for CylinderGeometry: [side, top, bottom]
        const texturedMaterials = [sideMaterial, faceMaterial, faceMaterial];

        const leftPileX = -1.5;
        const rightPileX = 1.5;
        const leftCount = 14;
        const rightCount = 5;

        // Helper to create a single coin
        const addCoin = (x: number, index: number, delayFrames: number) => {
            // Use plain material for the first coin (bottom), textured array for others
            const material = texturedMaterials;

            const mesh = new THREE.Mesh(coinGeometry, material);
            mesh.castShadow = true;
            mesh.receiveShadow = true;

            // Start high up with slight random shift for realism
            const startY = 12;
            const shiftAmount = 0.15;
            const randomX = (Math.random() - 0.5) * shiftAmount;
            const randomZ = (Math.random() - 0.5) * shiftAmount;
            mesh.position.set(x + randomX, startY, randomZ);

            // Random initial rotation
            mesh.rotation.x = (Math.random() - 0.5) * 1.0;
            mesh.rotation.z = (Math.random() - 0.5) * 1.0;
            mesh.rotation.y = Math.random() * Math.PI * 2;

            this.scene.add(mesh);

            // Target Y position (stacking)
            const targetY = index * coinHeight + (coinHeight / 2);

            this.coins.push({
                mesh: mesh,
                targetY,
                velocity: 0,
                state: 'waiting',
                delay: delayFrames,
                rotationSpeed: {
                    x: (Math.random() - 0.5) * 0.1,
                    z: (Math.random() - 0.5) * 0.1
                }
            });
        };

        for (let i = 0; i < leftCount; i++) {
            addCoin(leftPileX, i, i * 15);
        }

        for (let i = 0; i < rightCount; i++) {
            addCoin(rightPileX, i, i * 15 + 20);
        }
    }

    private animate = () => {
        this.animationId = requestAnimationFrame(this.animate);

        const gravity = 0.015;
        const bounceFactor = 0.3;

        this.coins.forEach(coin => {
            if (coin.state === 'waiting') {
                if (coin.delay > 0) {
                    coin.delay--;
                } else {
                    coin.state = 'falling';
                }
                return;
            }

            if (coin.state === 'falling' || coin.state === 'bouncing') {
                coin.velocity += gravity;
                coin.mesh.position.y -= coin.velocity;

                // Rotate while falling
                if (coin.state === 'falling') {
                    coin.mesh.rotation.x += coin.rotationSpeed.x;
                    coin.mesh.rotation.z += coin.rotationSpeed.z;

                    // Stabilize as it gets close
                    const dist = coin.mesh.position.y - coin.targetY;
                    if (dist < 2.0) {
                        coin.mesh.rotation.x *= 0.85;
                        coin.mesh.rotation.z *= 0.85;
                    }
                }

                // Collision
                if (coin.mesh.position.y <= coin.targetY) {
                    coin.mesh.position.y = coin.targetY;

                    if (Math.abs(coin.velocity) > 0.05) {
                        // Bounce
                        coin.velocity = -coin.velocity * bounceFactor;
                        coin.state = 'bouncing';
                        coin.mesh.rotation.x = 0;
                        coin.mesh.rotation.z = 0;
                    } else {
                        // Landed
                        coin.velocity = 0;
                        coin.state = 'landed';
                        coin.mesh.rotation.x = 0;
                        coin.mesh.rotation.z = 0;
                    }
                }
            }
        });

        this.renderer.render(this.scene, this.camera);
    }

    private handleResize = () => {
        if (!this.container) return;
        const width = this.container.clientWidth;
        const height = this.container.clientHeight;

        // Prevent 0 size issues
        if (width === 0 || height === 0) return;

        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(width, height);
    }

    public dispose() {
        if (this.animationId !== null) {
            cancelAnimationFrame(this.animationId);
        }
        window.removeEventListener('resize', this.handleResize);

        this.disposables.forEach(d => d.dispose());
        this.renderer.dispose();
        if (this.container.contains(this.renderer.domElement)) {
            this.container.removeChild(this.renderer.domElement);
        }
    }
}

interface CoinObject {
    mesh: THREE.Mesh;
    targetY: number;
    velocity: number;
    state: 'waiting' | 'falling' | 'bouncing' | 'landed';
    delay: number;
    rotationSpeed: { x: number, z: number };
}
